import React, { useMemo } from "react";
import { Box, Stack, Typography, Card, IconButton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useFormikContext } from "formik";
// Tree view replaced by 5-column card layout

const CategoriesTab = ({ filteredCategories, product, defaultLanguage }) => {
  const { values, setFieldValue } = useFormikContext();

  const tree = useMemo(() => {
    const byParent = {};
    (filteredCategories || []).forEach((cat) => {
      const parent = cat.parentCategory || "__root__";
      if (!byParent[parent]) byParent[parent] = [];
      byParent[parent].push(cat);
    });
    const build = (parentId) =>
      (byParent[parentId] || []).map((cat) => ({
        ...cat,
        children: build(cat._id),
      }));
    return build("__root__");
  }, [filteredCategories]);

  const renderNode = (node, level = 0) => {
    const id = String(node._id);
    const checked = Boolean(values?.categories?.[id]);
    const displayName =
      (node?.name && (node.name[defaultLanguage] || node.name)) || "";
    const nameText = typeof displayName === "string" ? displayName : "";
    const label = (
      <Box
        sx={{
          pl: level ? 1 : 0,
          py: 0.75,
          px: 1,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor: checked ? "#7352C7" : "transparent",
          color: checked ? "common.white" : "text.primary",
          "&:hover": { bgcolor: checked ? "#7352C7" : "action.hover" },
        }}
        onClick={() =>
          setFieldValue("categories", {
            ...(values?.categories || {}),
            [id]: !checked,
          })
        }
      >
        <Stack spacing={0.25}>
          <Typography variant="body2">{nameText}</Typography>
          <Typography
            variant="caption"
            sx={{ color: "inherit", fontSize: "0.5rem" }}
          >{`ID: ${id}`}</Typography>
        </Stack>
      </Box>
    );
    return (
      <Stack key={id} spacing={0.5}>
        {label}
        {(node.children || []).map((child) => renderNode(child, level + 1))}
      </Stack>
    );
  };

  return (
    <Box maxHeight="58vh" height="100%" px={1} sx={{ overflowY: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(6, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {tree.map((n) => {
          const rootId = String(n._id);
          const rootActive = Boolean(values?.categories?.[rootId]);
          return (
            <Card
              key={rootId}
              sx={{
                p: 1,
                position: "relative",
                bgcolor: rootActive ? "#7352C7" : "background.paper",
                color: rootActive ? "common.white" : "inherit",
                "&:hover": {
                  bgcolor: rootActive ? "#7352C7" : "action.hover",
                  cursor: "pointer",
                },
              }}
            >
              <IconButton
                component="a"
                href={`/customer/dsok/categories/${rootId}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  color: "inherit",
                }}
                aria-label="Open in new tab"
                title="Open in new tab"
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
              {renderNode(n)}
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default CategoriesTab;
